import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getStoredLanguage, saveLanguagePreference, DEFAULT_LANGUAGE } from '../utils/i18nConfig';
import api from '../api/axiosInstance';

// ── Async thunks for API calls ──────────────────────────────
export const fetchI18nConfigThunk = createAsyncThunk(
  'i18n/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/tenant/i18n/config');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || err.message);
    }
  }
);

export const updateLanguagePreferenceThunk = createAsyncThunk(
  'i18n/updateLanguagePreference',
  async ({ primaryLanguage, secondaryLanguage }, { rejectWithValue }) => {
    try {
      const response = await api.put('/tenant/i18n/language-preferences', { primaryLanguage, secondaryLanguage });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || err.message);
    }
  }
);

export const enableI18nForTenantThunk = createAsyncThunk(
  'i18n/enableI18nForTenant',
  async ({ tenantId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/super-admin/tenants/${tenantId}/i18n/enable`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || err.message);
    }
  }
);

export const disableI18nForTenantThunk = createAsyncThunk(
  'i18n/disableI18nForTenant',
  async ({ tenantId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/super-admin/tenants/${tenantId}/i18n/disable`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || err.message);
    }
  }
);

// ── Initial state ────────────────────────────────────────────
const initialState = {
  // User's current language preference
  currentLanguage: getStoredLanguage(),

  // Tenant-level i18n configuration (comes from backend at login)
  i18nEnabled: false,
  supportedLanguages: ['en'], // Languages available for this tenant
  primaryLanguage: 'en',
  secondaryLanguage: null,

  // Granular i18n configuration — which UI elements to translate
  i18nTranslateMenus: true,    // Translate navigation menus (default: true)
  i18nTranslateButtons: false, // Translate buttons (default: false)
  i18nTranslateForms: false,   // Translate form fields (default: false)
  i18nTranslateErrors: false,  // Translate error messages (default: false)

  // UI state
  loading: false,
  error: null,
  successMessage: null,
};

// ── Slice ────────────────────────────────────────────────────
const i18nSlice = createSlice({
  name: 'i18n',
  initialState,

  reducers: {
    // Set current user language (used for UI rendering)
    setCurrentLanguage(state, { payload }) {
      state.currentLanguage = payload;
      saveLanguagePreference(payload);
    },

    // Set tenant configuration (called during login)
    setTenantI18nConfig(state, { payload }) {
      const primaryLanguage = payload?.primaryLanguage || 'en';
      const secondaryLanguage = payload?.secondaryLanguage || null;
      const supportedLanguages = Array.isArray(payload?.supportedLanguages) && payload.supportedLanguages.length > 0
        ? payload.supportedLanguages
        : [primaryLanguage, secondaryLanguage].filter(Boolean);

      state.i18nEnabled = payload?.i18nEnabled ?? false;
      state.supportedLanguages = supportedLanguages.length > 0 ? supportedLanguages : ['en'];
      state.primaryLanguage = primaryLanguage;
      state.secondaryLanguage = secondaryLanguage;
      // Granular configuration (use payload values if available, else defaults)
      state.i18nTranslateMenus = payload?.i18nTranslateMenus !== undefined ? payload.i18nTranslateMenus : true;
      state.i18nTranslateButtons = payload?.i18nTranslateButtons !== undefined ? payload.i18nTranslateButtons : false;
      state.i18nTranslateForms = payload?.i18nTranslateForms !== undefined ? payload.i18nTranslateForms : false;
      state.i18nTranslateErrors = payload?.i18nTranslateErrors !== undefined ? payload.i18nTranslateErrors : false;

      // Keep UI language aligned with tenant-configured active languages.
      if (!state.supportedLanguages.includes(state.currentLanguage)) {
        state.currentLanguage = primaryLanguage;
        saveLanguagePreference(primaryLanguage);
      }
    },

    // Clear error message
    clearError(state) {
      state.error = null;
    },

    // Clear success message
    clearSuccessMessage(state) {
      state.successMessage = null;
    },

    // Reset to default
    resetI18n(state) {
      state.currentLanguage = DEFAULT_LANGUAGE;
      state.i18nEnabled = false;
      state.supportedLanguages = ['en'];
      state.primaryLanguage = 'en';
      state.secondaryLanguage = null;
      state.i18nTranslateMenus = true;
      state.i18nTranslateButtons = false;
      state.i18nTranslateForms = false;
      state.i18nTranslateErrors = false;
      state.loading = false;
      state.error = null;
      state.successMessage = null;
    },
  },

  extraReducers: (builder) => {
    // Fetch i18n config
    builder
      .addCase(fetchI18nConfigThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchI18nConfigThunk.fulfilled, (state, { payload }) => {
        const primaryLanguage = payload?.primaryLanguage || 'en';
        const secondaryLanguage = payload?.secondaryLanguage || null;
        const supportedLanguages = Array.isArray(payload?.supportedLanguages) && payload.supportedLanguages.length > 0
          ? payload.supportedLanguages
          : [primaryLanguage, secondaryLanguage].filter(Boolean);

        state.loading = false;
        state.i18nEnabled = payload?.i18nEnabled ?? false;
        state.supportedLanguages = supportedLanguages.length > 0 ? supportedLanguages : ['en'];
        state.primaryLanguage = primaryLanguage;
        state.secondaryLanguage = secondaryLanguage;
        state.i18nTranslateMenus = payload?.i18nTranslateMenus !== undefined ? payload.i18nTranslateMenus : state.i18nTranslateMenus;
        state.i18nTranslateButtons = payload?.i18nTranslateButtons !== undefined ? payload.i18nTranslateButtons : state.i18nTranslateButtons;
        state.i18nTranslateForms = payload?.i18nTranslateForms !== undefined ? payload.i18nTranslateForms : state.i18nTranslateForms;
        state.i18nTranslateErrors = payload?.i18nTranslateErrors !== undefined ? payload.i18nTranslateErrors : state.i18nTranslateErrors;

        if (!state.supportedLanguages.includes(state.currentLanguage)) {
          state.currentLanguage = primaryLanguage;
          saveLanguagePreference(primaryLanguage);
        }
      })
      .addCase(fetchI18nConfigThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // Update language preference
    builder
      .addCase(updateLanguagePreferenceThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLanguagePreferenceThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.primaryLanguage = payload.primaryLanguage;
        state.secondaryLanguage = payload.secondaryLanguage;
        state.supportedLanguages = [payload.primaryLanguage];
        if (payload.secondaryLanguage) {
          state.supportedLanguages.push(payload.secondaryLanguage);
        }
        state.successMessage = payload.message || 'Languages updated successfully';
      })
      .addCase(updateLanguagePreferenceThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // Enable i18n for tenant
    builder
      .addCase(enableI18nForTenantThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enableI18nForTenantThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.i18nEnabled = true;
        state.successMessage = payload.message || 'i18n enabled for tenant';
      })
      .addCase(enableI18nForTenantThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // Disable i18n for tenant
    builder
      .addCase(disableI18nForTenantThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disableI18nForTenantThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.i18nEnabled = false;
        state.successMessage = payload.message || 'i18n disabled for tenant';
      })
      .addCase(disableI18nForTenantThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });
  },
});

// ── Actions ──────────────────────────────────────────────────
export const { 
  setCurrentLanguage, 
  setTenantI18nConfig, 
  clearError, 
  clearSuccessMessage,
  resetI18n 
} = i18nSlice.actions;

// ── Selectors ────────────────────────────────────────────────
export const selectCurrentLanguage = (state) => state.i18n.currentLanguage;
export const selectI18nEnabled = (state) => state.i18n.i18nEnabled;
export const selectSupportedLanguages = (state) => state.i18n.supportedLanguages;
export const selectPrimaryLanguage = (state) => state.i18n.primaryLanguage;
export const selectSecondaryLanguage = (state) => state.i18n.secondaryLanguage;
export const selectI18nLoading = (state) => state.i18n.loading;
export const selectI18nError = (state) => state.i18n.error;
export const selectI18nSuccessMessage = (state) => state.i18n.successMessage;

// Granular i18n configuration selectors
export const selectI18nTranslateMenus = (state) => state.i18n.i18nTranslateMenus;
export const selectI18nTranslateButtons = (state) => state.i18n.i18nTranslateButtons;
export const selectI18nTranslateForms = (state) => state.i18n.i18nTranslateForms;
export const selectI18nTranslateErrors = (state) => state.i18n.i18nTranslateErrors;

// Selector to get all active languages (primary + secondary if available)
export const selectActiveLanguages = (state) => {
  const languages = [state.i18n.primaryLanguage];
  if (state.i18n.secondaryLanguage) {
    languages.push(state.i18n.secondaryLanguage);
  }
  return languages;
};

export default i18nSlice.reducer;
